
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from '@/lib/navigation';
import {
  BookOpen,
  FileText,
  Tags,
  Zap,
  ArrowUpRight,
} from 'lucide-react';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useCms } from '@/components/painel/cms/cms-context';
import { QUICK_ACTIONS } from '@/components/painel/dashboard/painel-role-dashboard';
import { usePowersCms } from '@/components/painel/powers/powers-cms-context';
import { useAcesCms } from '@/components/painel/aces/aces-cms-context';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelPermissionItemEnabledClass,
  painelPermissionsModalScrollClass,
} from '@/components/painel/painel-styles';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { flattenNavForRole } from '@/lib/painel-nav-rbac';
import {
  buildPainelSearchContentEntries,
  filterPainelSearchEntries,
  matchesPainelSearchQuery,
  PAINEL_SEARCH_KIND_LABELS,
  type PainelSearchContentEntry,
  type PainelSearchContentKind,
} from '@/lib/painel-search';
import type { PainelRole } from '@/lib/painel-rbac';
import { cn } from '@/lib/utils';

export type PainelCommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const EXTRA_LINKS: Array<{
  roles: PainelRole[];
  label: string;
  href: string;
  keywords?: string;
}> = [
  { roles: ['super_admin', 'admin', 'editor'], label: 'Novo guia', href: '/painel/guias/new', keywords: 'criar artigo' },
  { roles: ['super_admin', 'admin', 'editor'], label: 'Novo power', href: '/painel/powers/new', keywords: 'criar power' },
  { roles: ['super_admin', 'admin'], label: 'Ir ao site público', href: '/', keywords: 'help center' },
];

const CONTENT_KIND_ICONS: Record<PainelSearchContentKind, typeof BookOpen> = {
  guia: BookOpen,
  power: Zap,
  ace: AceNavIcon,
  categoria: Tags,
  pagina: FileText,
};

/** Borda sempre presente (transparente) evita “pulo” no hover/seleção. */
const commandItemClass = cn(
  'flex items-center gap-3 rounded-2xl border border-transparent px-3 py-2.5',
  'transition-[background-color,border-color,color] duration-200 ease-out',
  'hover:!border-sky-400/20 hover:!bg-blue-950/45 hover:!text-blue-50',
  "data-[selected=true]:!border-sky-400/25 data-[selected='true']:!border-sky-400/25",
  "data-[selected=true]:!bg-blue-950/60 data-[selected='true']:!bg-blue-950/60",
  "data-[selected=true]:!text-blue-50 data-[selected='true']:!text-blue-50",
  'data-[selected=true]:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
);

const commandItemStateOverrides = cn(
  '[&_[cmdk-item]]:border [&_[cmdk-item]]:border-transparent',
  '[&_[cmdk-item][data-selected=true]]:!border-sky-400/25 [&_[cmdk-item][data-selected=true]]:!bg-blue-950/60',
  "[&_[cmdk-item][data-selected='true']]:!border-sky-400/25 [&_[cmdk-item][data-selected='true']]:!bg-blue-950/60",
  '[&_[cmdk-item][data-selected=true]]:!text-blue-50 [&_[cmdk-item][data-selected=true]]:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
  "[&_[cmdk-item][data-selected='true']]:!text-blue-50",
  '[&_[cmdk-item]:hover]:!border-sky-400/20 [&_[cmdk-item]:hover]:!bg-blue-950/45 [&_[cmdk-item]:hover]:!text-blue-50'
);

const searchInputShellClass = cn(
  'rounded-xl border border-[#1e3a8a]/60 bg-blue-950/55',
  'shadow-[inset_0_1px_0_rgba(56,189,248,0.06)]',
  'transition-[border-color,background-color,box-shadow] duration-200 ease-out',
  'focus-within:border-sky-500/35 focus-within:bg-blue-950/68',
  'focus-within:shadow-[0_0_22px_-10px_rgba(56,189,248,0.45),inset_0_1px_0_rgba(56,189,248,0.08)]',
  '[&_[cmdk-input-wrapper]]:border-0 [&_[cmdk-input-wrapper]]:bg-transparent',
  '[&_[cmdk-input-wrapper]]:px-3.5 [&_[cmdk-input-wrapper]]:py-0',
  '[&_[cmdk-input-wrapper]_svg]:text-sky-400/75',
  '[&_[cmdk-input]]:h-11 [&_[cmdk-input]]:text-blue-50 [&_[cmdk-input]]:placeholder:text-slate-500/90'
);

const commandClass = cn(
  'relative flex h-full flex-col bg-transparent text-slate-100',
  commandItemStateOverrides,
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5',
  '[&_[cmdk-group-heading]]:font-inter [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.14em] [&_[cmdk-group-heading]]:text-slate-500',
  '[&_[cmdk-group]]:px-1 [&_[cmdk-group]]:text-slate-200',
  '[&_[cmdk-item]]:rounded-2xl'
);

function groupContentByKind(entries: PainelSearchContentEntry[]) {
  const groups = new Map<PainelSearchContentKind, PainelSearchContentEntry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.kind) ?? [];
    list.push(entry);
    groups.set(entry.kind, list);
  }
  return groups;
}

export function PainelCommandPalette({ open, onOpenChange }: PainelCommandPaletteProps) {
  const router = useRouter();
  const { role } = usePainelSession();
  const { articles, categories, getCategoryById } = useCms();
  const { powers } = usePowersCms();
  const { aces } = useAcesCms();
  const [query, setQuery] = useState('');

  const navItems = useMemo(() => (role ? flattenNavForRole(role) : []), [role]);

  const contentEntries = useMemo(() => {
    if (!role) return [];
    return buildPainelSearchContentEntries({
      role,
      articles,
      categories,
      powers,
      aces,
      getCategoryName: (id) => getCategoryById(id)?.name,
    });
  }, [role, articles, categories, powers, aces, getCategoryById]);

  const quickActions = useMemo(() => {
    if (!role) return [];
    const keys = {
      super_admin: ['newGuide', 'newPower', 'newCategory'] as const,
      admin: ['newGuide', 'newPower', 'newCategory'] as const,
      editor: ['newGuide', 'newPower', 'newPage'] as const,
      reviewer: ['reviewContent', 'approvePublish'] as const,
    }[role];
    return keys.map((key) => QUICK_ACTIONS[key]);
  }, [role]);

  const extraLinks = useMemo(
    () => (role ? EXTRA_LINKS.filter((l) => l.roles.includes(role)) : []),
    [role]
  );

  const filteredContent = useMemo(
    () => filterPainelSearchEntries(contentEntries, query, 40),
    [contentEntries, query]
  );

  const q = query.trim();

  const visibleQuickActions = useMemo(
    () =>
      q
        ? quickActions.filter((a) => matchesPainelSearchQuery(q, a.label, a.sub))
        : quickActions,
    [quickActions, q]
  );

  const visibleNavItems = useMemo(
    () =>
      q ? navItems.filter((i) => matchesPainelSearchQuery(q, i.label, i.href)) : navItems,
    [navItems, q]
  );

  const visibleExtraLinks = useMemo(
    () =>
      q
        ? extraLinks.filter((l) => matchesPainelSearchQuery(q, l.label, l.keywords ?? ''))
        : extraLinks,
    [extraLinks, q]
  );

  const hasResults =
    visibleQuickActions.length > 0 ||
    filteredContent.length > 0 ||
    visibleNavItems.length > 0 ||
    visibleExtraLinks.length > 0;

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [onOpenChange]);

  function go(href: string) {
    onOpenChange(false);
    router.push(href);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'max-h-[90vh] max-w-xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none sm:rounded-2xl',
          'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98]'
        )}
      >
        <DialogTitle className="sr-only">Buscar no painel</DialogTitle>
        <PainelPermissionsDialogShell>
          <Command className={commandClass} shouldFilter={false}>
            <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-4 pt-5 pr-14 sm:px-8 sm:pr-16">
              <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                Busca rápida
              </p>
              <h2 className="mt-1 font-poppins text-lg font-bold text-white sm:text-xl">
                Buscar no painel
              </h2>
              <p className="mt-1 font-inter text-xs text-slate-400">
                Guias, powers, aces, páginas e atalhos do seu cargo.
              </p>
              <div className={cn('mt-4', searchInputShellClass)}>
                <CommandInput
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Digite para filtrar…"
                />
              </div>
            </div>

            <div className="relative max-h-[min(52vh,380px)] bg-[#030023]/30">
              <CommandList
                className={cn(
                  'max-h-[min(52vh,380px)] overflow-y-auto px-4 py-3 sm:px-6',
                  painelPermissionsModalScrollClass
                )}
              >
              {q && !hasResults ? (
                <div className="py-10 text-center font-inter text-sm text-slate-500">
                  Nenhum resultado para «{q}».
                </div>
              ) : null}

              {visibleQuickActions.length > 0 ? (
                <CommandGroup heading="Ações rápidas" className="text-slate-200">
                  {visibleQuickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <CommandItem
                        key={action.href}
                        value={`${action.label} ${action.sub}`}
                        onSelect={() => go(action.href)}
                        className={commandItemClass}
                      >
                        <span
                          className={cn(
                            'flex h-8 w-8 items-center justify-center rounded-lg ring-1',
                            painelPermissionItemEnabledClass
                          )}
                        >
                          <Icon className="h-4 w-4 text-sky-200" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-blue-50">{action.label}</span>
                          <span className="block truncate text-xs text-slate-500">{action.sub}</span>
                        </span>
                        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}

              {filteredContent.length > 0
                ? Array.from(groupContentByKind(filteredContent).entries()).map(([kind, items]) => {
                    const Icon = CONTENT_KIND_ICONS[kind];
                    return (
                      <CommandGroup
                        key={kind}
                        heading={PAINEL_SEARCH_KIND_LABELS[kind]}
                        className="text-slate-200"
                      >
                        {items.map((entry) => (
                          <CommandItem
                            key={entry.id}
                            value={`${entry.label} ${entry.description} ${entry.keywords}`}
                            onSelect={() => go(entry.href)}
                            className={commandItemClass}
                          >
                            <span
                              className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1',
                                painelPermissionItemEnabledClass
                              )}
                            >
                              <Icon className="h-4 w-4 text-sky-200" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-blue-50">{entry.label}</span>
                              <span className="block truncate text-xs text-slate-500">{entry.description}</span>
                            </span>
                            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  })
                : null}

              {visibleNavItems.length > 0 ? (
                <>
                  <CommandSeparator className="my-2 bg-white/[0.06]" />
                  <CommandGroup heading="Navegação" className="text-slate-200">
                    {visibleNavItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <CommandItem
                          key={item.href}
                          value={`${item.label} ${item.href}`}
                          onSelect={() => go(item.href)}
                          className={commandItemClass}
                        >
                          <Icon className="h-4 w-4 shrink-0 text-sky-400/75" />
                          <span className="flex-1 text-blue-50">{item.label}</span>
                          <span className="font-mono text-[10px] text-slate-600">
                            {item.href.replace('/painel', '') || '/'}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              ) : null}

              {visibleExtraLinks.length > 0 ? (
                <>
                  <CommandSeparator className="my-2 bg-white/[0.06]" />
                  <CommandGroup heading="Atalhos" className="text-slate-200">
                    {visibleExtraLinks.map((link) => (
                      <CommandItem
                        key={link.href}
                        value={`${link.label} ${link.keywords ?? ''}`}
                        onSelect={() => go(link.href)}
                        className={commandItemClass}
                      >
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-sky-400/75" />
                        <span>{link.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              ) : null}
            </CommandList>
            </div>

            <div className="flex flex-col gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-3.5 font-inter text-[10px] text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <span>Navegue com ↑ ↓ · Enter para abrir</span>
              <div className="flex items-center gap-1.5">
                <kbd className="rounded-md border border-[#1e3a8a]/50 bg-[#030023]/50 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">
                  Ctrl
                </kbd>
                <kbd className="rounded-md border border-[#1e3a8a]/50 bg-[#030023]/50 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">
                  K
                </kbd>
              </div>
            </div>
          </Command>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
